import React, { useState, useEffect } from "react";
import axios from "axios";
import { AuthGuard } from "../App";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Oval } from "react-loader-spinner";
import { RoleGuard } from "../App";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faHandshake, // Icon for settlement
  faCalendarDay, // For individual dates or this year
  faFileInvoiceDollar, // For amount
  faBuilding, // For property
  faInfoCircle, // For errors/info
  faArchive, // For archived data button
  faMoneyBillWave, // Another option for money
  faCalendarAlt, // For general calendar year
} from "@fortawesome/free-solid-svg-icons";
import { StandardURL } from "../App";

const Settlement = () => {
  const [settlementData, setSettlementData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null); // New state for fetch errors
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSettlementData = async () => {
      setLoading(true);
      setFetchError(null); // Clear any previous errors

      const token = localStorage.getItem("token");
      const userDataString = localStorage.getItem("user");
      const userData = JSON.parse(userDataString);
      const userId = userData.unique; // Retained as per original code, even if not directly used in the API call below
      const propertyId = localStorage.getItem("selectedPropertyId");

      if (!token || !propertyId) {
        setFetchError("Authentication token or property ID missing. Please log in and select a property.");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `${StandardURL}/iNyumba/admin/settlementcheck/landlordthisyear/${propertyId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setSettlementData(Array.isArray(response.data.response) ? response.data.response : []);
        if (Array.isArray(response.data.response) && response.data.response.length === 0) {
          toast.info("No settlement records found for this year.");
        } else {
          toast.success("Settlement records fetched successfully!");
        }
      } catch (error) {
        console.error("Error fetching settlements:", error);
        if (axios.isAxiosError(error) && error.response && error.response.status === 404) {
          setFetchError("No settlement records found for this year for the selected property.");
          toast.info("No settlement records found for this year.");
        } else {
          setFetchError(error.response?.data?.response || "Failed to fetch settlement data.");
          toast.error(error.response?.data?.response || "Failed to fetch settlement data.");
        }
        setSettlementData([]); // Clear data on error
      } finally {
        setLoading(false);
      }
    };

    fetchSettlementData();
  }, [navigate]); // Added navigate to dependency array for completeness, though not strictly needed for this effect

  const handleArchivedData = () => {
    navigate("/landlord/archiveddatasettlement");
  };

  const getStatusColor = (status) => { // This function was missing in the context where the <td> elements were provided
    switch (status) {
      case "Approved":
        return "bg-green-100 text-green-800";
      case "Pending":
        return "bg-yellow-100 text-yellow-800";
      case "Rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };


  return (
    <RoleGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
          <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

          <div className="max-w-6xl mx-auto bg-white rounded-xl shadow-lg p-6 sm:p-8 lg:p-10">
            {/* Header and Back Button */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-8 border-b pb-4">
              <button
                onClick={() => navigate("/landlord/home")}
                className="flex items-center text-gray-600 hover:text-gray-800 transition-colors duration-200 mb-4 sm:mb-0"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Back to Dashboard
              </button>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-800 text-center flex-grow">
                <FontAwesomeIcon icon={faHandshake} className="mr-3 text-purple-600" />
                Current Year Settlements
              </h1>
              <div className="w-auto sm:w-1/4"></div> {/* Spacer for alignment */}
            </div>

            {/* View Archived Data Button */}
            <div className="mb-8 text-center">
              <button
                onClick={handleArchivedData}
                className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center mx-auto"
              >
                <FontAwesomeIcon icon={faArchive} className="mr-2" />
                View Archived Settlement Data
              </button>
            </div>

            {loading && (
              <div className="flex flex-col items-center justify-center py-10">
                <Oval
                  height={50}
                  width={50}
                  color="#8B5CF6" // Purple-500
                  wrapperStyle={{}}
                  wrapperClass=""
                  visible={true}
                  ariaLabel='loading-indicator'
                  secondaryColor="#E9D5FF" // Purple-200
                  strokeWidth={4}
                  strokeWidthSecondary={4}
                />
                <p className="ml-3 text-lg text-gray-700 mt-3">Fetching current year settlement data...</p>
              </div>
            )}

            {fetchError && !loading && (
              <div className="text-center py-10 bg-red-50 rounded-lg border border-red-300 text-red-700">
                <FontAwesomeIcon icon={faInfoCircle} className="text-5xl text-red-400 mb-4" />
                <p className="text-lg font-semibold mb-2">Error!</p>
                <p className="text-md">{fetchError}</p>
                <button
                  onClick={() => window.location.reload()} // Simple refresh to try again
                  className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-300"
                >
                  Retry
                </button>
              </div>
            )}

            {!loading && !fetchError && settlementData.length > 0 ? (
              <div className="overflow-x-auto mt-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                  Settlement Records for Current Year ({settlementData.length})
                </h2>
                <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden shadow-md">
                  <thead className="bg-gradient-to-r from-purple-600 to-purple-700 text-white">
                    <tr>
                      <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                        Settlement ID
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                        Total Amount Paid
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                        Transaction Cost
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                        Landlord Reference
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                        Processor Reference
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                        Mode
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                        Status
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold uppercase tracking-wider">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {settlementData.map((settlement, index) => (
                      <tr
                        key={settlement.settlement_id || index}
                        className={index % 2 === 0 ? "bg-gray-50 hover:bg-gray-100" : "bg-white hover:bg-gray-100"}
                      >
                        <td className="py-3 px-4 whitespace-nowrap text-gray-800">
                            {settlement.settlement_id || "N/A"}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-gray-800">
                            KES {parseFloat(settlement.total_amount_paid || 0).toLocaleString()}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-gray-800">
                            KES {parseFloat(settlement.transactioncost || 0).toLocaleString()}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-gray-800">
                            {settlement.reference || "N/A"}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-gray-800">
                            {settlement.paymentreference || "N/A"}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-gray-800">
                            {settlement.mode || "N/A"}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(settlement.status)}`}>
                                {settlement.status || "N/A"}
                            </span>
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-gray-600 text-sm">
                            {settlement.created_at ? new Date(settlement.created_at).toLocaleDateString("en-US", { year: 'numeric', month: 'short', day: 'numeric' }) : "N/A"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              !loading && !fetchError && (
                <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <FontAwesomeIcon icon={faInfoCircle} className="text-5xl text-gray-400 mb-4" />
                  <p className="text-lg text-gray-600">No settlement records found for the current year.</p>
                  <p className="text-md text-gray-500 mt-2">
                    Ensure the correct property is selected or view{" "}
                    <span onClick={handleArchivedData} className="text-blue-600 cursor-pointer hover:underline">
                      archived data
                    </span>.
                  </p>
                </div>
              )
            )}
          </div>
        </div>
      </AuthGuard>
    </RoleGuard>
  );
};

export default Settlement;